import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { get, isEmpty, isEqual, isUndefined } from 'lodash';
import { formatMoney, getCurrencySymbol } from '../../../utils/helpers';
import { pointUnits, rewardCategory, rewardRules, walletBenefitType } from '../config';
import rewardsRoutes from '../../../routes/rewards';
import resourcesConfig from '../../../resources';

export const renderPointUnit = (pointUnit, ruleCurrencyCode = '') => {
  switch (pointUnit) {
    case pointUnits.percent:
      return '%';
    case pointUnits.absolute:
      return getCurrencySymbol(ruleCurrencyCode);
    default:
      return;
  }
};

export const renderRewardRule = ({
  view,
  rule,
  minRedeemPrice,
  promotion,
  currencyName,
  itemTagsIdentifier = {},
}) => {
  const ruleCurrencyCode = get(rule, 'currencyCode', '');
  const {
    category,
    valueUnit,
    earn,
    maxEarn,
    maxEarnUnit,
    spend,
    subType,
    type,
    additionalDataJson,
    additionalData, // accept either shape
    minSpend,
    deals,
  } = rule || {};

  // Accept either additionalDataJson (string) or additionalData (object)
  let rawAdditional = additionalDataJson || additionalData || '';
  let parsedAdditional = {};

  if (rawAdditional) {
    try {
      parsedAdditional =
        typeof rawAdditional === 'string' ? JSON.parse(rawAdditional) : rawAdditional;
    } catch (err) {
      console.warn('DEBUG renderRewardRule: failed to parse additionalData (malformed JSON)', err);
    }
  } else {
    console.log('DEBUG renderRewardRule: no additionalDataJson or additionalData present on rule');
  }

  const ruleVariations = get(parsedAdditional, 'ruleVariations', []);
  const maxRedeemablePointsPer = get(parsedAdditional, 'maxReddemablePointsPer', 0);
  const maxRedeemablePoints = get(parsedAdditional, 'maxRedeemableAmount', 0);

  // keep the rest of your original function behavior below unchanged...
  const renderMaxRedeemablePoints = () => (
    <>
      {maxRedeemablePointsPer > 0 && (
        <>
          <br />
          <FormattedMessage
            id="maxRedeemablePointsPer"
            defaultMessage={resourcesConfig.maxRedeemablePointsPer}
            values={{
              maxValue: maxRedeemablePointsPer,
              currencyName,
            }}
          />
        </>
      )}
      {maxRedeemablePoints > 0 && (
        <>
          <br />
          <FormattedMessage
            id="maxRedeemablePoints"
            defaultMessage={resourcesConfig.maxRedeemablePoints}
            values={{
              maxValue: maxRedeemablePoints,
              currencyName,
            }}
          />
        </>
      )}
    </>
  );

  switch (category) {
    case 'EARN':
      if (isEqual(type, rewardRules.fixedPoints)) {
        return (
          <FormattedMessage
            id={minSpend ? 'collectPointWithFixedAndMinSpend' : 'collectPointinFixed'}
            defaultMessage={
              minSpend
                ? resourcesConfig.collectPointWithFixedAndMinSpend
                : resourcesConfig.collectPointinFixed
            }
            values={{
              earn,
              minSpend,
              currencyName: currencyName || resourcesConfig.rewardPoints,
              br: <br />,
            }}
          />
        );
      } else if (
        isEqual(subType, 'selected_item') &&
        !isEqual(view, rewardsRoutes.containers.home)
      ) {
        const rules = [];

        if (!ruleVariations || ruleVariations.length === 0) {
          return (
            <FormattedMessage
              id="spendAndEarnPointOnSelectedItems"
              defaultMessage={resourcesConfig.spendAndEarnPointOnSelectedItems}
              values={{
                earn,
                unit: renderPointUnit(valueUnit, ruleCurrencyCode),
                spend,
              }}
            />
          );
        }

        ruleVariations.forEach((variation, idx) => {
          const preCondition = get(variation, 'rulePreConditions', [])[0];
          const tag = get(preCondition, 'tags', [])[0];

          // ✅ Ensure fallback to readable text
          const displayTag = get(itemTagsIdentifier, tag, '') || tag || resourcesConfig.anyItem;

          rules.push(
            <FormattedMessage
              key={idx}
              id="earnPointOnSelectedItems"
              defaultMessage={resourcesConfig.earnPointOnSelectedItems}
              values={{
                tag: displayTag,
                currencyName: currencyName || resourcesConfig.rewardPoints,
                earn: variation.earn || earn,
              }}
            />,
          );
        });

        return rules;
      } else {
        switch (valueUnit) {
          case pointUnits.absolute:
            if (isEqual(view, rewardsRoutes.containers.home)) {
              if (!isEmpty(currencyName)) {
                return (
                  <FormattedMessage
                    id="collectPointWithCurrency"
                    defaultMessage={resourcesConfig.collectPointWithCurrency}
                    values={{
                      earn,
                      unit: renderPointUnit(valueUnit, ruleCurrencyCode),
                      spend,
                      currencyName,
                    }}
                  />
                );
              } else {
                return (
                  <FormattedMessage
                    id="collectPoint"
                    defaultMessage={resourcesConfig.collectPoint}
                    values={{ earn, unit: renderPointUnit(valueUnit, ruleCurrencyCode), spend }}
                  />
                );
              }
            } else {
              return (
                <FormattedMessage
                  id="spendAndEarnPoint"
                  defaultMessage={resourcesConfig.spendAndEarnPoint}
                  values={{ earn, unit: renderPointUnit(valueUnit, ruleCurrencyCode), spend }}
                />
              );
            }

          case pointUnits.percent:
            return (
              <FormattedMessage
                id="collectPointInPer"
                defaultMessage={resourcesConfig.collectPointInPer}
                values={{
                  earn,
                  unit: renderPointUnit(valueUnit, ruleCurrencyCode),
                  currencyName: currencyName || resourcesConfig.rewardPoints,
                  coinEarn: Math.floor(earn),
                }}
              />
            );
          default:
            return;
        }
      }

    case 'REDEEM':
      switch (type) {
        case rewardCategory.pointOffer:
          switch (maxEarnUnit) {
            case pointUnits.absolute:
              if (!isEmpty(currencyName)) {
                return (
                  <>
                    <FormattedMessage
                      id="useEarnPointsWithCurrency"
                      defaultMessage={resourcesConfig.useEarnPointsWithCurrency}
                      values={{ currencyName }}
                    />
                    {renderMaxRedeemablePoints()}
                  </>
                );
              } else {
                return (
                  <>
                    <FormattedMessage
                      id="useEarnPoints"
                      defaultMessage={resourcesConfig.useEarnPoints}
                    />
                    {renderMaxRedeemablePoints()}
                  </>
                );
              }
            case pointUnits.percent:
              return (
                <>
                  <FormattedMessage
                    id="userEarnPointInPer"
                    defaultMessage={resourcesConfig.userEarnPointInPer}
                    values={{
                      spend: maxEarn,
                      unit: renderPointUnit(maxEarnUnit, ruleCurrencyCode),
                    }}
                  />
                  {renderMaxRedeemablePoints()}
                </>
              );
            default:
              return;
          }
        case rewardCategory.rewardCouponInstant: // New Type Added Here
          const dealTitle = get(deals, '[0].title', '');

          return (
            <>
              <FormattedMessage
                id="instantCouponMessage" // Ensure this ID exists in your translations
                defaultMessage={resourcesConfig.instantCouponMessage}
                values={{
                  title: dealTitle,
                }}
              />
            </>
          );
        default:
          if (isEqual(view, rewardsRoutes.containers.home)) {
            return (
              <FormattedMessage
                id="unlockRewardOn"
                defaultMessage={resourcesConfig.unlockRewardOn}
                values={{ minRedeemPrice }}
              />
            );
          } else {
            return (
              <FormattedMessage
                id={promotion ? 'minRedeemPointsPromotion' : 'minRedeemPoints'}
                defaultMessage={
                  promotion
                    ? resourcesConfig.minRedeemPointsPromotion
                    : resourcesConfig.minRedeemPoints
                }
                values={{ minRedeemPrice }}
              />
            );
          }
      }

    default:
      return null;
  }
};

export const appResolutionConfig = {
  webLoyaltyFlow: 'WEB_LOYALTY_FLOW',
  appDownload: 'APP_DOWNLOAD',
  webOrderFlow: 'WEB_ORDER_FLOW',
  webOrderFlowWithLoyalty: 'WEB_ORDER_FLOW_WITH_LOYALTY',
};

export const renderReferralRule = ({
  view,
  referrerRule,
  referrerMilestoneRule,
  gainerRule,
  dealName,
}) => {
  // Function to map rule types to their display names
  const renderRuleType = (ruleType) => {
    const typeMap = {
      COUPON: 'Coupon', // Maps 'COUPON' to 'Coupon'
      POINT: 'Point', // Maps 'POINT' to 'Point'
    };
    return typeMap[ruleType] || ''; // Returns mapped type or an empty string if not found
  };

  // Function to get the formatted message for a given rule
  const getFormattedMessage = (prefix, rule) => {
    if (!rule) return null;
    // Get the display name for the rule type (Coupon or Point)
    const ruleType = renderRuleType(rule.type);

    const showMaxThresold = !isUndefined(rule.maxThreshold) && rule.maxThreshold > 0;

    // Constructs the message ID using prefix, rule type, and view type
    // Example IDs generated:
    // - If view = 'card' and ruleType = 'Point' → 'referralPointCardRule'
    // - If view is not 'card' and ruleType = 'Coupon' → 'referralCouponRule'
    // - If showMaxThresold is true → 'referralMilestonePointMaxThresoldRule'
    const id = `${prefix}${ruleType}${showMaxThresold ? 'MaxThresold' : ''}${view === 'card' ? 'CardRule' : 'Rule'
      }`;

    // Returns the FormattedMessage component with values for dealName and rewardPoints
    return (
      <FormattedMessage
        id={id}
        defaultMessage={resourcesConfig[id]} // Fetches the default message from resourcesConfig
        values={{
          dealName, // Retrieves the deal name if applicable
          rewardPoints: get(rule, 'value'), // Retrieves the reward points value
          thresold: get(rule, 'threshold', 0),
          maxThreshold: get(rule, 'maxThresold'),
        }}
      />
    );
  };

  // Determines which rule is available and returns the corresponding formatted message
  if (referrerRule) return getFormattedMessage('referral', referrerRule);
  if (referrerMilestoneRule) return getFormattedMessage('referralMilestone', referrerMilestoneRule);
  if (gainerRule) return getFormattedMessage('gainer', gainerRule);

  // If no rule is provided, return null (renders nothing)
  return null;
};

export const guestConsentList = [
  { key: 'storePi', isRequired: true },
  {
    key: 'txnCommunication',
    isRequired: true,
    channels: ['txnSms', 'txnEmail', 'txnPush', 'txnWhatsapp', 'txnRcs'],
    policyModeKey: 'txnPolicyMode',
  },
  {
    key: 'mktCommunication',
    isRequired: false,
    channels: ['mktSms', 'mktEmail', 'mktPush', 'mktWhatsapp', 'mktRcs'],
    policyModeKey: 'mktPolicyMode',
  },
];

export const renderWalletBenefitShortText = (benefit) => {
  const benefitCurrencyCode =
    get(benefit, 'currencyCode', '') || get(benefit, 'benefitRule.currencyCode', '');
  const benefitType = get(benefit, 'type');
  const groupingType = get(benefit, 'groupingType');
  const benefitRule = get(benefit, 'benefitRule', {});
  const value = get(benefitRule, 'value', 0);
  const valueUnit = get(benefitRule, 'valueUnit');
  const minAmount = get(benefitRule, 'minAmount');
  const dealId = get(benefitRule, 'dealId');
  const dealTitle = get(benefitRule, 'dealTitle', '');
  const couponCount = get(benefitRule, 'couponCount', 1);

  const hasTier = groupingType === 'TIER' && minAmount;
  const formattedMinAmount =
    minAmount != null ? formatMoney(minAmount, benefitCurrencyCode) : '';

  const formatMessage = (id, values) => {
    return <FormattedMessage id={id} defaultMessage={resourcesConfig[id]} values={values} />;
  };

  switch (benefitType) {
    case walletBenefitType.walletBonus: {
      if (valueUnit === pointUnits.percent) {
        if (hasTier) {
          return formatMessage('walletBenefitShortTextWalletBonusPerTier', {
            value,
            formattedMinAmount,
          });
        }
        return formatMessage('walletBenefitShortTextWalletBonusPer', { value });
      }

      if (hasTier) {
        return formatMessage('walletBenefitShortTextWalletBonusAbsTier', {
          value,
          formattedMinAmount,
        });
      }

      return formatMessage('walletBenefitShortTextWalletBonusAbs', { value });
    }

    case walletBenefitType.loyaltyBonus: {
      if (valueUnit === pointUnits.percent) {
        if (hasTier) {
          return formatMessage('walletBenefitShortTextLoyaltyBonusPerTier', {
            value,
            formattedMinAmount,
          });
        }
        return formatMessage('walletBenefitShortTextLoyaltyBonusPer', { value });
      }

      if (hasTier) {
        return formatMessage('walletBenefitShortTextLoyaltyBonusAbsTier', {
          value,
          formattedMinAmount,
        });
      }

      return formatMessage('walletBenefitShortTextLoyaltyBonusAbs', { value });
    }

    case walletBenefitType.coupon: {
      if (dealTitle) {
        if (hasTier) {
          return couponCount > 1
            ? formatMessage('walletBenefitShortTextMultipleCouponWithTitleTier', {
                count: couponCount,
                dealTitle,
                formattedMinAmount,
              })
            : formatMessage('walletBenefitShortTextCouponWithTitleTier', { dealTitle, formattedMinAmount });
        }
        return couponCount > 1
          ? formatMessage('walletBenefitShortTextMultipleCouponWithTitle', { count: couponCount, dealTitle })
          : formatMessage('walletBenefitShortTextCouponWithTitle', { dealTitle });
      }

      if (dealId) {
        return hasTier
          ? formatMessage('walletBenefitShortTextCouponGenericTier', { formattedMinAmount })
          : formatMessage('walletBenefitShortTextCouponGeneric');
      }

      return null;
    }

    default:
      return null;
  }
};
